import React, { useMemo, useState } from 'react';
import { Paper, Box, Switch, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { CategoryRounded } from '@mui/icons-material';
import { FilterBar, Field, SearchInput, SelectInput, Spacer, PageSub } from '../../components/common';
import DataTable, { Column } from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import { useToast } from '../../components/Toast';
import { useCategories, CategoryItem } from '../../hooks/useCategories';

const CategoriesPage: React.FC = () => {
  const toast = useToast();
  const { items, loading, error, reload, toggle, toggleConsultation } = useCategories();
  const [q, setQ] = useState('');
  const [state, setState] = useState('');

  const handleToggle = async (c: CategoryItem, on: boolean) => {
    const ok = await toggle(c.id, on);
    toast(ok ? `${c.name} ${on ? 'enabled' : 'disabled'}` : `Failed to update ${c.name}`, ok ? 'success' : 'error');
  };

  const handleConsultationToggle = async (c: CategoryItem, on: boolean) => {
    const ok = await toggleConsultation(c.id, on);
    toast(ok ? `Consultation ${on ? 'enabled' : 'disabled'} for ${c.name}` : `Failed to update ${c.name}`, ok ? 'success' : 'error');
  };

  const rows = useMemo(
    () => items.filter((c) => (!q || c.name.toLowerCase().includes(q.toLowerCase())) && (!state || (state === 'enabled') === c.enabled)),
    [items, q, state]
  );

  const columns: Column<CategoryItem>[] = [
    {
      key: 'name', label: 'Category', csv: (r) => r.name, render: (r) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: r.color, color: '#fff', '& svg': { fontSize: 17 } }}>
            <CategoryRounded />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>{r.name}</Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>{r.description}</Typography>
          </Box>
        </Box>
      ),
    },
    { key: 'businesses', label: 'Businesses', align: 'right' },
    { key: 'enabled', label: 'State', csv: (r) => (r.enabled ? 'Enabled' : 'Disabled'), render: (r) => <StatusChip status={r.enabled ? 'enabled' : 'disabled'} label={r.enabled ? 'Enabled' : 'Disabled'} /> },
    { key: '_', label: 'Action', render: (r) => <Switch checked={r.enabled} onChange={(e) => handleToggle(r, e.target.checked)} color="success" /> },
    {
      key: 'consultation', label: 'Consultation',
      csv: (r) => (r.consultation_enabled ? 'Enabled' : 'Disabled'),
      render: (r) => <Switch checked={r.consultation_enabled} onChange={(e) => handleConsultationToggle(r, e.target.checked)} color="primary" />,
    },
  ];

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300, alignItems: 'center' }}><CircularProgress /></Box>;
  }
  if (error) {
    return <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>{error}</Alert>;
  }

  return (
    <>
      <PageSub>
        Categories are pre-seeded. You can <b>enable</b> or <b>disable</b> them — disabled categories are hidden from
        customers in the app. No add / edit / delete.
      </PageSub>

      <FilterBar>
        <Field label="Search"><SearchInput value={q} onChange={setQ} placeholder="Category…" /></Field>
        <Field label="State"><SelectInput value={state} onChange={setState} options={[{ value: '', label: 'All' }, { value: 'enabled', label: 'Enabled' }, { value: 'disabled', label: 'Disabled' }]} /></Field>
        <Spacer />
      </FilterBar>

      <Paper sx={{ p: 0 }}>
        <DataTable columns={columns} rows={rows} title="Service Categories" exportName="categories" pageSize={10} />
      </Paper>
    </>
  );
};

export default CategoriesPage;
